// The pipeline's speedometer (v1.80.0): where this project's work actually
// sits, in plain sentences and honest bars — so fixing the pipeline aims at
// the genuinely slow step, not the loudest complaint. Numbers are medians
// over the last 90 days; stages need a few finished sits to be measured, and
// the panel says so out loud instead of quietly dropping them.
import { useEffect, useState } from 'react';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { X } from 'lucide-react';
import type { ProjectPace } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { Spinner } from '@/core/ui/atoms';

function fmtDays(d: number): string {
  if (d < 0.75) return 'under a day';
  const shown = String(d < 10 ? Math.round(d * 10) / 10 : Math.round(d));
  return `${shown} ${shown === '1' ? 'day' : 'days'}`;
}

export function PacePanel({ areaId, areaName, onClose }: { areaId: number; areaName: string; onClose: () => void }) {
  const [pace, setPace] = useState<ProjectPace | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(onClose);

  useEffect(() => {
    let dead = false;
    api.get<{ pace: ProjectPace }>(`/areas/${areaId}/pace`)
      .then((r) => { if (!dead) setPace(r.pace); })
      .catch((e) => { if (!dead) setError(e instanceof Error ? e.message : 'Could not load the numbers.'); });
    return () => { dead = true; };
  }, [areaId]);

  const maxDays = pace?.stages.length ? Math.max(...pace.stages.map((s) => s.medianDays), 0.1) : 1;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border border-line bg-canvas p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-ink">How work moves — {areaName}</h2>
          <button onClick={onClose} className="rounded p-1 text-faint hover:text-ink" title="Close"><Icon i={X} size={14} /></button>
        </div>
        <p className="mb-4 text-[11px] text-faint">Typical times over the last {pace?.windowDays ?? 90} days.</p>

        {error && <p className="text-[13px] text-danger">{error}</p>}
        {!error && !pace && <div className="flex justify-center py-8"><Spinner /></div>}

        {pace && (
          <>
            <p className="mb-4 text-[13.5px] leading-relaxed text-ink">
              {pace.finishedMedianDays !== null ? (
                <>Work started in the last {Math.round(pace.startWindowDays / 30)} months that finished here usually took <strong>{fmtDays(pace.finishedMedianDays)}</strong> start to finish — measured over {pace.finishedCount.toLocaleString()} card{pace.finishedCount === 1 ? '' : 's'}.</>
              ) : (
                <>Not enough finished work in the last {pace.windowDays} days to measure a start-to-finish time yet.</>
              )}
            </p>

            {pace.stages.length > 0 ? (
              <div className="space-y-2">
                {pace.stages.map((s, i) => (
                  <div key={s.name}>
                    <div className="mb-0.5 flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate text-[12.5px] text-ink">{s.name}</span>
                      <span className="shrink-0 text-[12px] text-muted" title={`measured over ${s.visits} finished sit${s.visits === 1 ? '' : 's'}`}>
                        {fmtDays(s.medianDays)}
                        {i === 0 && pace.stages.length > 1 && <span className="ml-1.5 font-medium text-warn">← the slow step</span>}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-hover">
                      <div className={`h-full rounded-full ${i === 0 && pace.stages.length > 1 ? 'bg-warn' : 'bg-brand/60'}`}
                        style={{ width: `${Math.max(4, (s.medianDays / maxDays) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-muted">No step has enough finished visits in this window to measure yet.</p>
            )}

            <p className="mt-4 text-[11px] leading-snug text-faint">
              How to read this: each number is the middle of the pack (half sat shorter, half longer), counted from a card entering a step to it leaving. Steps need at least {pace.minSits} finished visits in the window to show — a number built on one card would be a story, not a measurement.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
